import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import DayModeSelector from './DayModeSelector';
import TaskList from './TaskList';
import QuoteCard from './QuoteCard';
import EnoughButton from './EnoughButton';
import { 
  getDayMode, 
  getTasks, 
  saveTasks,
  getNextQuoteIndex
} from '../utils/localStorage';
import { getDayModeConfig } from '../utils/dayModes';
import { quotes, getQuoteOfTheDay } from '../utils/quotes';
import { 
  Brain, 
  Moon, 
  Settings,
  ChevronRight,
  Compass,
  Sparkles,
  Calendar,
  Utensils,
  RefreshCw,
  Flower2
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dayMode, setDayMode] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [quote, setQuote] = useState(null);
  
  useEffect(() => {
    const mode = getDayMode();
    setDayMode(mode);
    const tasks = getTasks();
    setCompletedTasks(tasks);
    const quoteIndex = getNextQuoteIndex(quotes.length);
    setQuote(getQuoteOfTheDay(quoteIndex));
  }, []);
  
  const handleModeSelect = (mode) => {
    setDayMode(mode);
    setCompletedTasks([]);
    saveTasks([]);
    toast.success(`${getDayModeConfig(mode).name} selected`);
  };
  
  const handleToggleTask = (taskId) => {
    const newCompleted = completedTasks.includes(taskId)
      ? completedTasks.filter(id => id !== taskId)
      : [...completedTasks, taskId];
    
    setCompletedTasks(newCompleted);
    saveTasks(newCompleted);
    
    const modeConfig = getDayModeConfig(dayMode);
    const essentialTasks = modeConfig.tasks.filter(t => !t.skippable);
    const essentialDone = essentialTasks.every(t => newCompleted.includes(t.id));
    
    if (essentialDone && !completedTasks.includes(taskId)) {
      toast.success("All essentials done! You're doing great.");
    }
  };
  
  const handleEnoughClick = () => {
    navigate('/review');
  };
  
  const modeConfig = dayMode ? getDayModeConfig(dayMode) : null;
  
  return (
    <div className="min-h-screen bg-paper paper-texture" data-testid="dashboard-page">
      {/* Decorative herbs */}
      <div className="fixed top-20 right-4 text-3xl opacity-20 pointer-events-none">🌿</div>
      <div className="fixed bottom-32 left-4 text-2xl opacity-15 pointer-events-none">🍂</div>
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-paper/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brown flex items-center justify-center shadow-lifted">
                <Compass className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-lg text-ink-900">The Calm Keeper</h1>
                <p className="text-xs text-ink-500 font-accent italic">Compass</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                data-testid="nav-brain-dump"
                onClick={() => navigate('/brain-dump')}
                variant="ghost"
                size="icon"
                className="text-ink-500 hover:text-sage hover:bg-sage-mist"
              >
                <Brain className="w-5 h-5" />
              </Button>
              <Button
                data-testid="nav-resources"
                onClick={() => navigate('/resources')}
                variant="ghost"
                size="icon"
                className="text-ink-500 hover:text-brown hover:bg-brown/10"
              >
                <Sparkles className="w-5 h-5" />
              </Button>
              <Button
                data-testid="nav-review"
                onClick={() => navigate('/review')}
                variant="ghost"
                size="icon"
                className="text-ink-500 hover:text-sage hover:bg-sage-mist"
              >
                <Moon className="w-5 h-5" />
              </Button>
              <Button
                data-testid="nav-settings"
                onClick={() => navigate('/settings')}
                variant="ghost"
                size="icon"
                className="text-ink-500 hover:text-brown hover:bg-brown/10"
              >
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Quote of the day */}
        {quote && <QuoteCard quote={quote} />}
        
        {/* Day mode selector or task list */}
        {!dayMode ? (
          <div className="animate-fade-in-up">
            <DayModeSelector 
              currentMode={dayMode} 
              onModeSelect={handleModeSelect} 
            />
          </div>
        ) : (
          <div className="space-y-8 animate-fade-in">
            {/* Current mode badge with change option */}
            <div className="flex items-center justify-between">
              <div className={`
                inline-flex items-center gap-2 px-4 py-2 rounded-lg border
                ${modeConfig.bgColor} ${modeConfig.borderColor} ${modeConfig.textColor}
              `}>
                <span className="text-lg">{modeConfig.emoji}</span>
                <span className="font-serif font-medium">{modeConfig.name}</span>
              </div>
              
              <Button
                data-testid="change-day-mode"
                onClick={() => setDayMode(null)}
                variant="ghost"
                size="sm"
                className="text-ink-500 hover:text-brown font-accent italic"
              >
                Change mode
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {/* Task list */}
            <TaskList
              dayMode={dayMode}
              completedTasks={completedTasks}
              onToggleTask={handleToggleTask}
            />
            
            {/* Enough button */}
            <EnoughButton onClick={handleEnoughClick} />
          </div>
        )}
        
        {/* Decorative divider */}
        <div className="divider-herbs">
          <span>🌿</span>
        </div>
        
        {/* Quick navigation cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card 
            data-testid="quick-nav-brain-dump"
            className="p-4 bg-sage/20 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-sage/40 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/brain-dump')}
          >
            <Brain className="w-6 h-6 text-sage-dark mb-2" />
            <h3 className="font-serif font-medium text-sage-dark text-sm">Mental Offloader</h3>
            <p className="text-xs text-sage-dark/70 font-accent italic">Offload thoughts</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-meals"
            className="p-4 bg-brown/15 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-brown/30 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/meals')}
          >
            <Utensils className="w-6 h-6 text-brown mb-2" />
            <h3 className="font-serif font-medium text-brown-dark text-sm">Meal Calendar</h3>
            <p className="text-xs text-brown-dark/70 font-accent italic">Plan meals</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-resources"
            className="p-4 bg-sage/20 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-sage/40 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/resources')}
          >
            <Sparkles className="w-6 h-6 text-sage-dark mb-2" />
            <h3 className="font-serif font-medium text-sage-dark text-sm">Resources</h3>
            <p className="text-xs text-sage-dark/70 font-accent italic">AI ideas</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-planner"
            className="p-4 bg-brown/15 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-brown/30 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/planner')}
          >
            <Calendar className="w-6 h-6 text-brown mb-2" />
            <h3 className="font-serif font-medium text-brown-dark text-sm">Daily Planner</h3>
            <p className="text-xs text-brown-dark/70 font-accent italic">Print & plan</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-review"
            className="p-4 bg-sage/20 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-sage/40 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/review')}
          >
            <Moon className="w-6 h-6 text-sage-dark mb-2" />
            <h3 className="font-serif font-medium text-sage-dark text-sm">Review</h3>
            <p className="text-xs text-sage-dark/70 font-accent italic">Close your day</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-journal-calendar"
            className="p-4 bg-brown/15 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-brown/30 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/journal-calendar')}
          >
            <Flower2 className="w-6 h-6 text-brown mb-2" />
            <h3 className="font-serif font-medium text-brown-dark text-sm">My Journey</h3>
            <p className="text-xs text-brown-dark/70 font-accent italic">Grow your flower</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-weekly-reset"
            className="p-4 bg-sage/20 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-sage/40 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/weekly-reset')}
          >
            <RefreshCw className="w-6 h-6 text-sage-dark mb-2" />
            <h3 className="font-serif font-medium text-sage-dark text-sm">Weekly Reset</h3>
            <p className="text-xs text-sage-dark/70 font-accent italic">Fresh start</p>
          </Card>
          
          <Card 
            data-testid="quick-nav-settings"
            className="p-4 bg-brown/15 shadow-lifted cursor-pointer hover:shadow-lifted-lg transition-all border border-brown/30 hover:-translate-y-1 relative overflow-hidden"
            onClick={() => navigate('/settings')}
          >
            <Settings className="w-6 h-6 text-brown mb-2" />
            <h3 className="font-serif font-medium text-brown-dark text-sm">Settings</h3>
            <p className="text-xs text-brown-dark/70 font-accent italic">Customize</p>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
